import { useEffect, useRef, useState } from 'react';
import type { DragEvent, FormEvent } from 'react';
import { useGlobalStore, songKey } from '../../stores/global-store';
import { useUserStore } from '../../stores/user-store';
import { useModalStore } from '../../stores/modal-store';
import { useToastStore } from '../../stores/toast-store';
import { sendEventToServer } from '../../api/http-req';
import { useExportSpotifyPlaylist, useExportYoutubePlaylist } from '../../queries/playlists';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { environment } from '../../config/environment';
import { ModalType } from '../../interfaces/modal-types.enum';
import type { FinalSong } from '../../interfaces/final-song';
import type { PlaylistReturnObject, SongsNotFound } from '../../interfaces/playlist-return-object';
import AppImage from '../../shared/components/AppImage';
import Lottie from '../../shared/components/Lottie';
import emptyPlaylistAnimation from '../../assets/lottie/empty-playlist.json';
import SpinnerLoading from '../../shared/components/SpinnerLoading';
import SocialSharing from '../../shared/components/SocialSharing';
import Tooltip from '../../shared/components/Tooltip';
import './Playlist.scss';

// Port of src/app/components/playlist/ (mypart-playlist).
//
// - Angular host: class "flex flex-col flex-1 overflow-hidden" → root div class.
// - The constructor effect() on playlistSongs (reset to list tab, reset the
//   playlist-name field, rebuild the local songs array) → useEffect below.
// - loading$ Subject → boolean state.
// - playlistName FormControl(required) → controlled input; validity = non-empty.
// - NOTE(port): Angular used @angular/cdk/drag-drop (cdkDropList/cdkDrag) to
//   reorder the playlist. Ported as native HTML5 drag-and-drop on each <li>
//   (grab cursor + drop-target highlight), plus up/down move buttons as an
//   accessible fallback. Both call moveSong(), which performs the same
//   moveItemInArray + playlistSongs replacement (rAF deferral preserved).
// - notFoundOnExport pipe → isNotFoundOnExport() helper.
// - The "Could not find song on spotify" tippy used tpTrigger="manual" +
//   (click)="tooltip.show()" — ported as a click-triggered Tooltip.
// - getButtonColor() and the commented-out playSong()/exportToApple() bodies
//   were unused dead code in the Angular component — not ported.

function isNotFoundOnExport(song: FinalSong, songsNotFound: SongsNotFound[]): boolean {
  return !!songsNotFound.find((s) => song.artist === s.artist);
}

function getDate(): string {
  const currentDate = new Date();
  const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11
  const day = String(currentDate.getDate()).padStart(2, '0');
  const year = String(currentDate.getFullYear()).slice(-2); // Get last two digits of the year

  return `${month}/${day}/${year}`;
}

function convertToCSV(objArray: any[], headers: string[]): string {
  const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;

  let str = '';
  const headerRow = headers.map(header => `"${header}"`).join(','); // Add quotes to headers
  str += headerRow + '\r\n';

  array.forEach((item: any) => {
    let line = '';
    headers.forEach((header) => {
      if (line !== '') line += ',';

      let value = item[header] !== undefined ? String(item[header]) : '';
      // Escape double quotes and wrap value in quotes
      value = `"${value.replace(/"/g, '""')}"`;

      line += value;
    });
    str += line + '\r\n';
  });

  return str;
}

type Supplier = 'spotify' | 'youtube' | 'apple';

export default function Playlist() {
  const playlistSongs = useGlobalStore(s => s.playlistSongs);
  const isUserWhiteLabel = useUserStore(s => s.isUserWhiteLabel);
  const toast = useToastStore.getState();

  const [loading, setLoading] = useState(false);
  const [listTabSelected, setListTabSelected] = useState(true);
  const [songsArray, setSongsArray] = useState<FinalSong[]>([]);
  const [playlistName, setPlaylistName] = useState('');
  const [songsNotFound, setSongsNotFound] = useState<SongsNotFound[] | undefined>(undefined);
  const [showRemovePopup, setShowRemovePopup] = useState(false);
  // Native HTML5 drag-to-reorder (restores the CDK cdkDrag interaction).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [chosenSupplier, setChosenSupplier] = useState<Supplier | undefined>(undefined);
  const [playlistUrl, setPlaylistUrl] = useState<string | null>(null);
  const playlistIdRef = useRef<string | undefined>(undefined);
  const { mutateAsync: exportSpotifyAsync } = useExportSpotifyPlaylist();
  const { mutateAsync: exportYoutubeAsync } = useExportYoutubePlaylist();

  // Angular constructor effect(): on every playlistSongs change reset to the
  // list tab, reset the name field and rebuild the local array.
  useEffect(() => {
    setListTabSelected(true);
    setPlaylistName('');
    setSongsArray(Array.from(playlistSongs.values()));
  }, [playlistSongs]);

  const hidePlaylist = () => {
    useGlobalStore.getState().setGlobalField('showPlaylist', false);
  };

  const removeFromPlaylist = (song: FinalSong) => {
    const globalStore = useGlobalStore.getState();
    globalStore.addRemoveSongFromPlaylist(song);
    globalStore.removeSongAddedToPlaylist(song);
    if (useGlobalStore.getState().playlistSongs.size === 0) {
      hidePlaylist();
    }
  };

  const removeAllSongs = () => {
    const songs = Array.from(useGlobalStore.getState().playlistSongs.values());
    for (const song of songs) {
      removeFromPlaylist(song);
    }
  };

  // Reorder helper — shared by native drag-and-drop and the up/down buttons.
  // Mirrors Angular's onDrop: moveItemInArray + rAF playlistSongs replacement.
  const moveSong = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= songsArray.length || fromIndex === toIndex) return;

    const reordered = [...songsArray];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setSongsArray(reordered);
    requestAnimationFrame(() => {
      const newMap = new Map<string, FinalSong>();
      reordered.forEach(song => newMap.set(songKey(song), song));
      useGlobalStore.getState().setGlobalField('playlistSongs', newMap);
    });
  };

  // Native HTML5 drag-and-drop handlers (cdkDrag/cdkDropList equivalent).
  const onDragStart = (index: number) => (e: DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (index: number) => (e: DragEvent) => {
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragOverIndex) {
      setDragOverIndex(index);
    }
  };
  const onDrop = (index: number) => (e: DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      moveSong(dragIndex, index);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const onDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const exportPlaylist = async () => {
    setLoading(true);
    const fullPlaylistName = `${environment.defaultPlaylistName} | ${getDate()} | ${playlistName}`;
    const supplier = chosenSupplier;
    const globalStore = useGlobalStore.getState();

    try {
      let result: PlaylistReturnObject | undefined;
      switch (supplier) {
        case 'youtube':
          result = await exportYoutubeAsync({
            youtubeIds: globalStore.getSongsForYoutube(),
            name: fullPlaylistName,
            playlistId: playlistIdRef.current,
          });
          break;
        case 'apple':
          break;
        case 'spotify':
          result = await exportSpotifyAsync({
            songs: globalStore.getSongsForSpotify(),
            name: fullPlaylistName,
            playlistId: playlistIdRef.current,
          });
          break;
      }

      if (result) {
        if (result.error) {
          toast.error(`Could not create playlist. ${environment.toastSupportMessage}`);
        } else {
          playlistIdRef.current = result.playlistId;
          setPlaylistUrl(result.link);

          sendEventToServer('SH_PLAYLIST_SPOTIFY_EXPORT', {
            'email': useUserStore.getState().user!.email,
            'url': result.link,
            'playlistId': result.playlistId
          });
        }
        setSongsNotFound(result.songsNotFound);
      }
    } catch {
      toast.error(`Could not create playlist. ${environment.toastSupportMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (playlistName.trim()) {
      if (useUserStore.getState().user) {
        void exportPlaylist();
      } else {
        useModalStore.getState().open(ModalType.LOGIN_REGISTER, {
          inputs: {
            loginPopupTitle: 'Sign Up for FREE to Export Your Playlist to Spotify',
            onlyExplorer: true
          }
        });
      }
    }
    // Angular: playlistName.markAsTouched() — the template showed no error
    // styling, so the invalid case is a no-op here too.
  };

  const exportToCsv = () => {
    const songs = Array.from(useGlobalStore.getState().playlistSongs.values());
    const notFoundSongs = songsNotFound;

    const finalList = songs.map(song => {
      const url = song.youtube_id ? `https://www.youtube.com/watch?v=${song.youtube_id}` : '';

      const baseSong = {
        artist: song.artist,
        title: song.title,
        url: url // Set the URL based on the youtube_id
      };

      if (notFoundSongs) {
        const match = notFoundSongs.some(notFound => notFound.artist === song.artist);
        return { ...baseSong, notFoundOnSpotify: match || false };
      }

      return baseSong;
    });

    const headers = ['artist', 'title', 'url'];
    if (notFoundSongs) {
      headers.push('notFoundOnSpotify');
    }

    Promise.resolve(convertToCSV(finalList, headers))
      .then((csvData: string) => {
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', 'FinalListExport.csv');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Show a popup message
          toast.success('Your file has been downloaded. You can find it in your default download location.');
        }
      })
      .catch(error => {
        toast.error('Error exporting to CSV: ' + error);
      });
  };

  const emailPlaylist = (url: string) => {
    const subject = 'Check out my playlist!';
    const body = `Hey! I created a new playlist. Check it out here: ${url}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const supplier = chosenSupplier;

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div>
        <div className={`relative w-full h-full mx-auto ${loading ? 'blur-sm' : ''}`}>
          {/* Modal content */}
          <div className="relative">
            {/* Close button */}
            <button onClick={hidePlaylist}
                    className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 z-50
          hover:text-gray-900 rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center
          dark:hover:bg-gray-600 dark:hover:text-white" type="button">
              <svg aria-hidden="true" className="w-3 h-3" fill="none" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"/>
              </svg>
              <span className="sr-only">Close modal</span>
            </button>

            <div className="px-4 py-6 relative min-h-[100vh] grid grid-rows-[auto_1fr_auto]">
              {/* Tabs */}
              <div className={`w-full flex mb-8 text-xl font-medium text-center text-gray-500 border-b
            border-gray-200 dark:text-gray-400 dark:border-gray-700 ${showRemovePopup ? 'blur-sm' : ''}`}>
                <button onClick={() => setListTabSelected(true)}
                        className={`w-full mr-2 p-4 border-b-2 border-transparent rounded-t-lg ${listTabSelected
                          ? 'text-[#c42727] border-[#c42727] dark:text-[#c42727] dark:border-[#c42727]'
                          : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'}`}
                        type="button">
                  Playlist
                </button>
                {playlistSongs.size > 0 && (
                  <button onClick={() => setListTabSelected(false)}
                          className={`w-full ml-2 p-4 border-b-2 border-transparent rounded-t-lg ${!listTabSelected
                            ? 'text-[#c42727] border-[#c42727] dark:text-[#c42727] dark:border-[#c42727]'
                            : 'hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300'}`}
                          type="button">
                    Export
                  </button>
                )}
              </div>

              {/* List */}
              {listTabSelected && (
                <>
                  <div className="relative flex-1 -mx-4 overflow-y-auto h-[calc(100dvh-200px)]">
                    <ul className="divide-y divide-zinc-200 dark:divide-zinc-600 px-4 will-change-transform" role="list">
                      {songsArray.length > 0 ? songsArray.map((song, i) => (
                        <li key={song.id}
                            draggable
                            onDragStart={onDragStart(i)}
                            onDragOver={onDragOver(i)}
                            onDrop={onDrop(i)}
                            onDragEnd={onDragEnd}
                            className={`group hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-transform duration-150 ease-out
                           px-4 py-3 relative will-change-transform
                           ${dragIndex === i ? 'opacity-40' : ''}
                           ${dragOverIndex === i && dragIndex !== i ? 'border-t-2 border-[#c42727]' : ''}`}>
                          <div className="flex justify-between items-center">
                            <span className="w-[8%] flex items-center text-gray-400 dark:text-gray-500 font-medium">
                              {i + 1}
                            </span>
                            <div className="w-[82%] flex min-w-0 gap-x-4 items-center cursor-grab active:cursor-grabbing">
                              {/* NOTE(port): CDK drag handle replaced with up/down move buttons. */}
                              <div className="flex flex-col items-center justify-center">
                                <button onClick={() => moveSong(i, i - 1)}
                                        disabled={i === 0}
                                        className="p-0.5 text-gray-400 dark:text-gray-500 hover:text-[#c42727] disabled:opacity-30"
                                        title="Move song up" type="button">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M5 15l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                                  </svg>
                                </button>
                                <button onClick={() => moveSong(i, i + 1)}
                                        disabled={i === songsArray.length - 1}
                                        className="p-0.5 text-gray-400 dark:text-gray-500 hover:text-[#c42727] disabled:opacity-30"
                                        title="Move song down" type="button">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                                  </svg>
                                </button>
                              </div>
                              <div className="relative">
                                <AppImage alt={'Cover image for the song - ' + song.title + ', By - ' + song.artist}
                                          src={song.imageUrl}
                                          className="w-12 h-12 rounded-md object-cover shadow-sm
                                    transition-transform duration-200 group-hover:scale-105"
                                          height={48}
                                          width={48}/>
                                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 rounded-md
                                  transition-all duration-200 ease-in-out"></div>
                              </div>
                              <div className="min-w-0 flex-auto">
                                <div className="flex items-center gap-2">
                                  <span title={song.title}
                                        className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-[#c42727]
                                     transition-colors duration-200">
                                    {song.title}
                                  </span>
                                </div>
                                <span title={song.artist}
                                      className="text-xs text-gray-500 dark:text-gray-400 truncate block
                                   group-hover:text-gray-600 dark:group-hover:text-gray-300
                                   transition-colors duration-200">
                                  {song.artist}
                                </span>
                              </div>
                            </div>
                            <div className="w-[10%] flex items-center justify-end gap-2">
                              {songsNotFound && (
                                <Tooltip content="Could not find song on spotify" trigger="click">
                                  <svg aria-hidden="true"
                                       className={`w-5 h-5 cursor-pointer text-[#c42727] hidden
                                    hover:scale-110 transition-transform duration-200 ${isNotFoundOnExport(song, songsNotFound) ? '!block' : ''}`}
                                       fill="none"
                                       viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 13V8m0 8h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" stroke="currentColor"
                                          strokeLinecap="round" strokeLinejoin="round"
                                          strokeWidth="2"/>
                                  </svg>
                                </Tooltip>
                              )}
                              <button onClick={() => removeFromPlaylist(song)}
                                      className="group-hover:opacity-100 opacity-0 transition-all duration-200
                                     hover:text-[#c42727] text-gray-400 dark:text-gray-500
                                     focus:outline-none focus:ring-2 focus:ring-[#c42727]/20 rounded-full p-1"
                                      title="Remove song from the playlist">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        strokeWidth="2"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#c42727]/10 scale-x-0 group-hover:scale-x-100
                             transition-transform duration-300 ease-out"></div>
                        </li>
                      )) : (
                        /* Angular @empty block */
                        <div className="flex flex-col items-center justify-center py-12 px-4">
                          <Lottie animationData={emptyPlaylistAnimation}
                                  className="w-32 h-32 mb-6"
                                  ariaLabel="Empty playlist"/>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
                            Your Playlist is Empty
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300 text-center mb-6">
                            Start building your perfect playlist! Search for your favorite songs and click the "+" button to add
                            them.
                          </p>
                          <div className="flex flex-col items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-[#c42727]/80 dark:text-[#c42727]/70" fill="none" stroke="currentColor"
                                   viewBox="0 0 24 24">
                                <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round"
                                      strokeWidth="2"/>
                              </svg>
                              <span>Search for songs using the search bar</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-[#c42727]/80 dark:text-[#c42727]/70" fill="none" stroke="currentColor"
                                   viewBox="0 0 24 24">
                                <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeLinecap="round" strokeLinejoin="round"
                                      strokeWidth="2"/>
                              </svg>
                              <span>Click the "+" button to add songs</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-[#c42727]/80 dark:text-[#c42727]/70" fill="none" stroke="currentColor"
                                   viewBox="0 0 24 24">
                                <path d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                      strokeLinecap="round" strokeLinejoin="round"
                                      strokeWidth="2"/>
                              </svg>
                              <span>Enjoy your music!</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </ul>
                  </div>

                  {playlistSongs.size > 0 && (
                    <>
                      <button onClick={() => setShowRemovePopup(true)}
                              className={`group relative w-full mt-6 px-4 py-3 text-sm font-medium
                           bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                           hover:border-red-200 dark:hover:border-red-900/50
                           hover:bg-red-50 dark:hover:bg-red-900/20
                           transition-all duration-200 ease-in-out
                           flex items-center justify-center gap-2 rounded-md ${showRemovePopup ? 'blur-sm' : ''}`} type="button">
                        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-500
                        transition-colors duration-200"
                             fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                strokeLinecap="round" strokeLinejoin="round"
                                strokeWidth="2"/>
                        </svg>
                        <span className="text-gray-700 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-500
                          transition-colors duration-200">
                          Remove all songs
                        </span>
                      </button>

                      <div className={`absolute hidden top-0 left-0 w-full h-full bg-black/50 backdrop-blur-sm
                 transition-opacity duration-300 ease-in-out
                 flex items-center justify-center z-50 ${showRemovePopup ? '!flex' : ''}`}>
                        <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-2xl transform transition-all duration-300 ease-in-out
                          max-w-md w-[90%] mx-4 overflow-hidden ${showRemovePopup ? '!scale-100 !opacity-100' : 'scale-95 opacity-0'}`}>
                          <div className="p-6">
                            <div className="flex items-center justify-center mb-4">
                              <div className="w-12 h-12 mb-6 relative">
                                <div className="absolute inset-0 bg-red-100 dark:bg-red-900/30 rounded-full"></div>
                                <svg className="w-6 h-6 text-red-600 dark:text-red-500 relative" fill="none" stroke="currentColor"
                                     viewBox="0 0 24 24">
                                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        strokeWidth="2"/>
                                </svg>
                              </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                              Remove All Songs?
                            </h3>
                            <p className="text-sm md:text-base text-gray-600 dark:text-gray-300 text-center mb-6">
                              This action cannot be undone. Are you sure you want to remove all songs from your playlist?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                              <button onClick={() => setShowRemovePopup(false)}
                                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border
                                   border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none
                                   focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors
                                   dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600
                                   dark:hover:bg-gray-600 dark:focus:ring-gray-500">
                                Cancel
                              </button>
                              <button onClick={removeAllSongs}
                                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600
                                   rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2
                                   focus:ring-offset-2 focus:ring-red-500 transition-colors
                                   dark:bg-red-500 dark:hover:bg-red-600">
                                Remove All
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Export */}
              {!listTabSelected && (
                <div className="flex flex-col w-full max-w-2xl mx-auto overflow-y-auto px-4 h-[calc(100dvh-200px)]">
                  <div className="text-center mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Export Your Playlist</h2>
                    {!chosenSupplier && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">Choose your preferred music platform</p>
                    )}
                  </div>

                  <div className="relative mt-4">
                    {/* Back button */}
                    {chosenSupplier && (
                      <button onClick={() => setChosenSupplier(undefined)}
                              className="absolute -top-12 left-0 flex items-center gap-2 text-gray-600 dark:text-gray-300
                               hover:text-gray-900 dark:hover:text-white transition-colors duration-200">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path d="M10 19l-7-7m0 0l7-7m-7 7h18" strokeLinecap="round" strokeLinejoin="round"
                                strokeWidth="2"/>
                        </svg>
                        <span className="text-sm">Back to platforms</span>
                      </button>
                    )}

                    {/* Supplier Grid */}
                    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${!chosenSupplier ? 'mb-8 grid' : 'block'}`}>
                      {/* Spotify */}
                      <button onClick={() => setChosenSupplier('spotify')}
                              className={`group relative flex flex-col items-center p-4 rounded-xl border border-gray-200
                               dark:border-gray-700 hover:border-[#1DB954] dark:hover:border-[#1DB954]
                               transition-all duration-300 ease-in-out
                               ${chosenSupplier === 'spotify' ? 'ring-2 ring-[#1DB954] w-full sm:w-80 mx-auto' : ''}
                               ${chosenSupplier && chosenSupplier !== 'spotify' ? 'hidden' : ''}`}>
                        <div className="w-12 h-12 mb-3">
                          <svg className="w-full h-full text-[#1DB954]" viewBox="0 0 168 168">
                            <path d="m83.996 0.277c-46.249 0-83.743 37.493-83.743 83.742 0 46.251 37.494 83.741 83.743 83.741 46.254 0 83.744-37.49 83.744-83.741 0-46.246-37.49-83.738-83.745-83.738l0.001-0.004zm38.404 120.78c-1.5 2.46-4.72 3.24-7.18 1.73-19.662-12.01-44.414-14.73-73.564-8.07-2.809 0.64-5.609-1.12-6.249-3.93-0.643-2.81 1.11-5.61 3.926-6.25 31.9-7.291 59.263-4.15 81.337 9.34 2.46 1.51 3.24 4.72 1.73 7.18zm10.25-22.805c-1.89 3.075-5.91 4.045-8.98 2.155-22.51-13.839-56.823-17.846-83.448-9.764-3.453 1.043-7.1-0.903-8.148-4.35-1.04-3.453 0.907-7.093 4.354-8.143 30.413-9.228 68.222-4.758 94.072 11.127 3.07 1.89 4.04 5.91 2.15 8.976v-0.001zm0.88-23.744c-26.99-16.031-71.52-17.505-97.289-9.684-4.138 1.255-8.514-1.081-9.768-5.219-1.254-4.14 1.08-8.513 5.221-9.771 29.581-8.98 78.756-7.245 109.83 11.202 3.73 2.209 4.95 7.016 2.74 10.733-2.2 3.722-7.02 4.949-10.73 2.739z"
                                  fill="currentColor"/>
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">Spotify</span>
                        <span className="text-xs text-green-600 mt-1">&nbsp;</span>
                      </button>

                      {/* YouTube */}
                      <button className={`group relative flex flex-col items-center p-4 rounded-xl border border-gray-200
                               dark:border-gray-700 hover:border-[#FF0000] dark:hover:border-[#FF0000]
                               transition-all duration-300 ease-in-out
                               ${chosenSupplier === 'youtube' ? 'ring-2 ring-[#FF0000] w-full sm:w-80 mx-auto' : ''}
                               ${chosenSupplier && chosenSupplier !== 'youtube' ? 'hidden' : ''}`}
                              disabled>
                        <div className="w-12 h-12 mb-3">
                          <svg className="w-full h-full text-[#FF0000]" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568H9.545zM5.818 12l6.273-3.568L5.818 8.432V15.568z"
                                  fill="currentColor"/>
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-400 dark:text-gray-500">YouTube</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">Coming Soon</span>
                      </button>

                      {/* Apple Music */}
                      <button className={`group relative flex flex-col items-center p-4 rounded-xl border border-gray-200
                               dark:border-gray-700 hover:border-black dark:hover:border-white
                               transition-all duration-300 ease-in-out
                               ${chosenSupplier === 'apple' ? 'ring-2 ring-black w-full sm:w-80 mx-auto' : ''}
                               ${chosenSupplier && chosenSupplier !== 'apple' ? 'hidden' : ''}`}
                              disabled>
                        <div className="w-12 h-12 mb-3">
                          <img alt="Apple Music"
                               className="w-full h-full dark:invert"
                               src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Apple_Music_logo.svg"/>
                        </div>
                        <span className="text-sm font-medium text-gray-400 dark:text-gray-500">Apple Music</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500 mt-1">Coming Soon</span>
                      </button>

                      {isUserWhiteLabel && (
                        /* CSV Export */
                        <button onClick={exportToCsv}
                                className="group relative flex flex-col items-center p-4 rounded-xl border border-gray-200
                                 dark:border-gray-700 hover:border-[#185C37] dark:hover:border-[#185C37]
                                 transition-all duration-200 focus:outline-none">
                          <div className="w-12 h-12 mb-3">
                            <svg className="w-full h-full text-gray-900 dark:text-white" fill="none" viewBox="0 0 24 24"
                                 xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M13 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V9M13 3V8C13 8.55228 13.4477 9 14 9H19"
                                  stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>
                              <path d="M12 12V18M12 18L9 15M12 18L15 15" stroke="currentColor" strokeLinecap="round"
                                    strokeLinejoin="round" strokeWidth="2"/>
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">CSV Export</span>
                          <span className="text-xs text-green-600 dark:text-green-400">Available</span>
                        </button>
                      )}
                    </div>

                    {/* Playlist Form */}
                    {supplier && (
                      <div className="w-full sm:w-80 mx-auto transition-all duration-300 ease-in-out mt-4 opacity-100 translate-y-0">
                        <form onSubmit={onSubmit} className="space-y-4">
                          <div className="relative">
                            <input value={playlistName}
                                   onChange={(e) => setPlaylistName(e.target.value)}
                                   className="block w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700
                                    focus:ring-2 focus:ring-[#1DB954] focus:border-transparent dark:bg-gray-800
                                    text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                                   name="playlistName" placeholder="Enter your playlist name"
                                   required
                                   type="text"/>
                          </div>

                          <button className={`w-full flex items-center justify-center px-4 py-3 rounded-lg
                                   text-white font-medium transition-colors duration-200
                                   focus:outline-none focus:ring-2 focus:ring-offset-2
                                   dark:focus:ring-offset-gray-900
                                   ${supplier === 'spotify' ? 'bg-[#1DB954] hover:bg-[#1DB954]/90' : ''}
                                   ${supplier === 'youtube' ? 'bg-[#FF0000] hover:bg-[#FF0000]/90' : ''}
                                   ${supplier === 'apple' ? 'bg-black hover:bg-black/90 dark:bg-white dark:hover:bg-white/90 dark:text-black' : ''}`}
                                  type="submit">
                            Create Playlist
                          </button>
                        </form>

                        {playlistUrl && (
                          <div
                              className="mt-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Playlist is Ready!</h3>
                              <a href={playlistUrl} className="text-sm text-[#1DB954] hover:text-[#1DB954]/80 font-medium"
                                 target="_blank" rel="noreferrer">
                                Open
                                in {supplier === 'spotify' ? 'Spotify' : supplier === 'youtube' ? 'YouTube' : 'Apple Music'}
                              </a>
                            </div>

                            <div className="relative flex items-center mb-4">
                              <input value={playlistUrl} className="block w-full pr-24 py-2 pl-3 rounded-lg border border-gray-200
                                      dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600
                                      dark:text-gray-300 text-sm" readOnly
                                     type="text"/>
                              <button onClick={() => copyToClipboard(toast, playlistUrl)}
                                      className="absolute right-2 px-3 py-1 text-xs font-medium text-[#1DB954]
                                       hover:text-[#1DB954]/80 transition-colors duration-200">
                                Copy Link
                              </button>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-4">
                              <SocialSharing link={playlistUrl} className="w-1/3 playlist-social-sharing"
                                             source="playlist"/>

                              <button onClick={() => emailPlaylist(playlistUrl)}
                                      className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700
                                       bg-white border border-gray-200 rounded-lg hover:bg-gray-50
                                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1DB954]
                                       dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700
                                       dark:hover:bg-gray-700 transition-all duration-200 ease-in-out">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2zM9 19V5l12-3"
                                        strokeLinecap="round" strokeLinejoin="round"
                                        strokeWidth="2"/>
                                </svg>
                                Email
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {loading && <SpinnerLoading/>}
      </div>
    </div>
  );
}
